import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CheckoutDto } from '@dto/checkoutDto';
import { PublicChargeSubscriptionDto as PublicTransactionRequestDto } from '@dto/publicChargeSubscriptionDto';
import { Observable } from 'rxjs';
import { UserDto } from '@dto/userDto';
import { StripeOnboardingDto } from '@dto/stripeOnboarding';
import { InvoiceDto } from '@dto/invoiceDto';
import { SubscriptionDto } from '@dto/subscriptionDto';
import { ChargingParkSubscriptionDto } from '@dto/chargingParkSubscriptionDto';
import { StripePublishableKeyDto } from '@dto/stripePublishableKeyDto';
import { PublicTransactionSessionDto } from '@dto/publicTransactionSessionDto';
import { PaymentReceiptDto } from '@dto/paymentReceiptDto';
import { CancelSubscriptionResponse } from '@dto/cancelSubscriptionResponse';
import { PaymentIntent } from '@stripe/stripe-js';

@Injectable({
  providedIn: 'root'
})
export class PaymentsService {

  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {}

  getStripePublishableKey(): Observable<StripePublishableKeyDto> {
    return this.http.get<StripePublishableKeyDto>(this.baseUrl + 'api/payments/stripe-publishable-key');
  }

  getCheckoutSession(sessionId: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'api/payments/checkout-session/' + sessionId);
  }

  getCustomer(customerId: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'api/payments/customer/' + customerId);}

  createCheckoutSession(checkout: CheckoutDto): Observable<CheckoutDto> {
    return this.http.post<CheckoutDto>(this.baseUrl + 'api/payments/create-checkout-session', checkout);
  }

  createCheckoutSessionForCUBOSConnect(checkout: CheckoutDto): Observable<CheckoutDto> {
    return this.http.post<CheckoutDto>(this.baseUrl + 'api/payments/create-checkout-session-for-cubos-connect', checkout);
  }

  createCheckoutSessionForUser(checkout: CheckoutDto): Observable<CheckoutDto> {
    return this.http.post<CheckoutDto>(this.baseUrl + 'api/payments/create-checkout-session-for-user', checkout);
  }

  onSuccess(sessionId: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'api/payments/success/' + sessionId, null);
  }

  patchChargingParkSubscriptionId(sessionId: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'api/payments/patch-charging-park-subscription-id/' + sessionId, null);
  }

  createStripeAccount(user: UserDto): Observable<StripeOnboardingDto> {
    return this.http.post<StripeOnboardingDto>(this.baseUrl + 'api/payments/create-stripe-account', user);
  }

  getInvoices(stripeCustomerId: string): Observable<InvoiceDto[]> {
    return this.http.get<InvoiceDto[]>(this.baseUrl + 'api/payments/invoices/' + stripeCustomerId);
  }

  getInvoicesBySessionId(sessionId: string): Observable<InvoiceDto[]> {
    return this.http.get<InvoiceDto[]>(this.baseUrl + 'api/payments/invoices/session/' + sessionId);
  }

  createSetupIntent(chargingParkSubscriptionDto: ChargingParkSubscriptionDto): Observable<ChargingParkSubscriptionDto> {
    return this.http.post<ChargingParkSubscriptionDto>(this.baseUrl + 'api/payments/charging-park/setupIntent/create', chargingParkSubscriptionDto);
  }

  createChargingParkSubscription(chargingParkSubscriptionDto: ChargingParkSubscriptionDto): Observable<ChargingParkSubscriptionDto> {
    return this.http.post<ChargingParkSubscriptionDto>(this.baseUrl + 'api/payments/charging-park/subscription/create', chargingParkSubscriptionDto);
  }

  createChargingParkSubscriptionByPaypal(setupIntentId: string): Observable<ChargingParkSubscriptionDto> {
    return this.http.post<ChargingParkSubscriptionDto>(this.baseUrl + 'api/payments/charging-park/subscription/create-paypal-callback/' + setupIntentId, null);
  }

  updateChargingParkSubscription(chargingParkSubscriptionDto: ChargingParkSubscriptionDto): Observable<ChargingParkSubscriptionDto> {
    return this.http.post<ChargingParkSubscriptionDto>(this.baseUrl + 'api/payments/charging-park/subscription/update', chargingParkSubscriptionDto);
  }

  createPublicTransactionPaymentIntent(publicTransactionRequestDto: PublicTransactionRequestDto): Observable<PublicTransactionRequestDto> {
    return this.http.post<PublicTransactionRequestDto>(this.baseUrl + 'api/payments/public-transaction/payment-intent/create', publicTransactionRequestDto);
  }

  updatePrecisionChargeAmount(paymentIntentId: string, maxAmount: number): Observable<PaymentIntent> {
    return this.http.patch<PaymentIntent>(`${this.baseUrl}api/payments/public-transaction/payment-intent/${paymentIntentId}/${maxAmount}`, null);
  }

  getSubscription(subscriptionId: string): Observable<SubscriptionDto> {
    return this.http.get<SubscriptionDto>(this.baseUrl + 'api/payments/subscription/' + subscriptionId);
  }

  cancelSubscription(userId: string): Observable<CancelSubscriptionResponse> {
    return this.http.put<CancelSubscriptionResponse>(this.baseUrl + 'api/payments/cancel-subscription/'+ userId, null);
  }

  cancelSubscriptionForCUBOSConnect(): Observable<Boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/payments/cancel-subscription-for-cubos-connect');
  }

  cancelPublicChargeSubscriptionAtPeriodEnd(subscriptionId: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'api/payments/cancel-public-charge-subscription-at-period-end/' + subscriptionId);
  }

  checkSubscriptionExpired(): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/payments/check-subscription-expired', null);
  }

  validatePublicTransaction(paymentIntentId: string): Observable<PublicTransactionSessionDto> {
    return this.http.get<PublicTransactionSessionDto>(this.baseUrl + 'api/payments/validatePublicTransaction/'+ paymentIntentId);
  }

  getPublicTransactionSession(publicTransactionId: number): Observable<PublicTransactionSessionDto> {
    return this.http.get<PublicTransactionSessionDto>(this.baseUrl + 'api/payments/publicTransactionSession/' + publicTransactionId);
  }

  getPaymentReceiptsOfCustomer(userId: string): Observable<PaymentReceiptDto[]> {
    return this.http.get<PaymentReceiptDto[]>(this.baseUrl + 'api/payments/getPaymentReceiptsOfCustomer/' + userId);
  }

  getPaymentReceiptsOfUser(userId: string): Observable<PaymentReceiptDto[]> {
    return this.http.get<PaymentReceiptDto[]>(this.baseUrl + 'api/payments/getPaymentReceiptsOfUser/' + userId);
  }

  getPaymentReceiptsOfSelectedCustomer(customerId: number): Observable<PaymentReceiptDto[]> {
    return this.http.get<PaymentReceiptDto[]>(this.baseUrl + 'api/payments/getPaymentReceiptsOfSelectedCustomer/' + customerId);
  }
}
